import axios from 'axios';

const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwt');

    // Добавляем токен ко всем запросам, если он есть
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle different error statuses
    if (error.response) {
      switch (error.response.status) {
        case 401:
          // Unauthorized - clear token
          localStorage.removeItem('jwt');
          localStorage.removeItem('favorites_cache');
          delete api.defaults.headers.common['Authorization'];
          break;
        case 403:
          break;
        case 404:
          break;
        case 409:
          break;
        case 500:
          break;
        default:
          break;
      }
    } else if (error.request) {
      // Network error - no response received
    } else {
      // Request error
    }
    return Promise.reject(error);
  }
);

// Auth helpers
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('jwt', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
};

export const removeAuthToken = () => {
  localStorage.removeItem('jwt');
  delete api.defaults.headers.common['Authorization'];
};

export const getAuthToken = () => {
  return localStorage.getItem('jwt');
};

// Authentication
/**
 * Отметка «пользователь здесь» — для статистики активности в админке.
 *
 * Тихая: ошибку глотаем намеренно. Пинг ничего не показывает на экране,
 * и ронять из-за него интерфейс или сыпать в консоль незачем.
 */
export const pingActivity = async () => {
  try {
    await api.post('/user-activity/ping');
  } catch {
    // сеть отвалилась или сессия истекла — следующий пинг разберётся
  }
};

/**
 * Пеший маршрут между точками — через наш бэкенд, а не напрямую в OSRM.
 *
 * Раньше браузер ходил на routing.openstreetmap.de сам, и позиция
 * пользователя уезжала туда в пути URL: точка «где человек стоит»
 * оседала в чужих логах вместе с его IP. Теперь наружу ходит сервер.
 *
 * @param {Array<[number, number]>} points пары [широта, долгота]
 * @returns {Promise<Array<[number, number]>|null>} линия или null,
 *          если маршрут не построился — тогда рисуем прямую
 */
export const fetchFootRoute = async (points) => {
  try {
    const { data } = await api.post('/routing/foot', { points });
    return data?.geometry ?? null;
  } catch {
    return null;
  }
};

export const authenticateTelegram = async (initData) => {
  try {
    const response = await api.post('/auth/telegram', { initData });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Categories
export const getCategories = async () => {
  try {
    const response = await api.get('/categories?filters[published][$eq]=true&sort=order:asc&populate=icon');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Places
export const getPlaces = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      'filters[published][$eq]': 'true',
      'populate[images][fields][0]': 'url',
      'populate[category][fields][0]': 'name',
      'populate[category][fields][1]': 'slug',
      'sort': 'createdAt:desc',
      ...params,
    });
    const response = await api.get(`/places?${queryParams}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getFeaturedPlaces = async () => {
  try {
    const response = await api.get('/places?filters[published][$eq]=true&filters[featured][$eq]=true&populate[images][fields][0]=url&populate[category][fields][0]=name&populate[category][fields][1]=slug');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPlacesByCategory = async (categorySlug) => {
  try {
    const response = await api.get(
      `/places?filters[published][$eq]=true&filters[category][slug][$eq]=${categorySlug}&populate[images][fields][0]=url&populate[category][fields][0]=name&populate[category][fields][1]=slug`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPlaceBySlug = async (slug) => {
  try {
    const response = await api.get(
      `/places?filters[published][$eq]=true&filters[slug][$eq]=${slug}&populate[category]=*&populate[tags]=*&populate[images]=*&populate[highlights][populate]=image&populate[workingHours]=*`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getPlaceById = async (id) => {
  try {
    const response = await api.get(`/places/${id}?populate[category]=*&populate[tags]=*&populate[images]=*&populate[highlights][populate]=image`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Tags
export const getTags = async (categoryFilter = null) => {
  try {
    let url = '/tags?populate=categories&sort=order:asc';
    if (categoryFilter) {
      url += `&filters[category][$eq]=${categoryFilter}`;
    }
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get tags for a specific category
 * @param {number} categoryId - Category ID
 * @returns {Promise} - Promise with tags data
 */
export const getCategoryTags = async (categoryId) => {
  try {
    const response = await api.get(`/categories/${categoryId}/tags`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return { data: [] };
    }
    throw error;
  }
};

/**
 * Get filtered places
 * @param {Object} params - Filter parameters
 * @param {number} params.categoryId - Category ID
 * @param {Array} params.tagIds - Array of tag IDs
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.pageSize - Page size (default: 25)
 * @returns {Promise} - Promise with filtered places data
 */
export const getFilteredPlaces = async ({ categoryId, tagIds = [], page = 1, pageSize = 25 }) => {
  try {
    const params = new URLSearchParams({
      'filters[category][id][$eq]': categoryId,
      'filters[published][$eq]': 'true',
      'pagination[page]': page,
      'pagination[pageSize]': pageSize,
    });

    // Add tag filters (AND logic)
    if (tagIds && tagIds.length > 0) {
      tagIds.forEach((tagId, index) => {
        params.append(`filters[tags][id][$in][${index}]`, tagId);
      });
    }

    const response = await api.get(`/places/filtered?${params.toString()}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return { data: [], meta: { pagination: { total: 0 } } };
    }
    throw error;
  }
};

/**
 * Get count of filtered places (lightweight query)
 * @param {Object} params - Filter parameters
 * @param {number} params.categoryId - Category ID
 * @param {Array} params.tagIds - Array of tag IDs
 * @returns {Promise<number>} - Promise with count
 */
export const getFilteredPlacesCount = async ({ categoryId, tagIds = [] }) => {
  try {
    const params = new URLSearchParams({
      'filters[category][id][$eq]': categoryId,
      'filters[published][$eq]': 'true',
      'pagination[page]': '1',
      'pagination[pageSize]': '1', // Minimal data
    });

    // Add tag filters
    if (tagIds && tagIds.length > 0) {
      tagIds.forEach((tagId, index) => {
        params.append(`filters[tags][id][$in][${index}]`, tagId);
      });
    }

    const response = await api.get(`/places/filtered?${params.toString()}`);
    return response.data.meta?.pagination?.total || 0;
  } catch (error) {
    return 0;
  }
};

// Hero Slides
export const getHeroSlides = async () => {
  try {
    const response = await api.get('/hero-slides?filters[published][$eq]=true&sort=order:asc&populate[image][fields][0]=url');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Ads
export const getAds = async (categoryId = null, location = 'categories') => {
  try {
    let url = '/ads?filters[published][$eq]=true&populate[image][fields][0]=url&populate[categories][fields][0]=id';

    // Фильтр по месту показа
    if (location === 'home') {
      url += '&filters[showOnHome][$eq]=true';
    } else if (location === 'categories') {
      url += '&filters[showInCategories][$eq]=true';
    } else if (location === 'nearby') {
      url += '&filters[showInNearby][$eq]=true';
    }

    if (categoryId) {
      url += `&filters[categories][id][$eq]=${categoryId}`;
    }

    const response = await api.get(url);
    return response.data;
  } catch (error) {
    return { data: [] };
  }
};

// Most Saved Places
/**
 * Получение самых популярных мест (по количеству добавлений в избранное)
 * @param {number} limit - Лимит результатов (default: 30)
 * @returns {Promise} - Promise with most saved places
 */
export const getMostSavedPlaces = async (limit = 30) => {
  try {
    const response = await api.get(`/places/most-saved?limit=${limit}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Места, которые чаще всего открывают из поиска.
 *
 * Это клики по местам, а не тексты запросов: запрос нигде не сохраняется.
 * minClicks — порог на минимум данных: пока кликов мало, порядок случаен,
 * и блок на странице поиска прячется целиком.
 *
 * @param {number} limit - Лимит результатов (default: 6)
 * @param {number} minClicks - Минимум кликов, чтобы место попало в выдачу
 * @returns {Promise} - Promise with places
 */
export const getTopFromSearchPlaces = async (limit = 6, minClicks = 3) => {
  try {
    const response = await api.get('/places/top-from-search', {
      params: { limit, minClicks },
    });
    return response.data;
  } catch (error) {
    // Блок необязательный: молчим и не показываем его
    return { data: [] };
  }
};

// Search
/**
 * Поиск мест
 * @param {string} query - Поисковый запрос
 * @param {Object} options - Опции поиска
 * @param {number} options.limit - Лимит результатов (default: 10)
 * @param {number} options.category - ID категории (optional)
 * @param {string} options.sort - Сортировка (optional)
 * @returns {Promise} - Promise with search results
 */
export const searchPlaces = async (query, options = {}) => {
  try {
    const { limit = 10, category, sort } = options;
    const params = new URLSearchParams({
      query,
      limit: limit.toString(),
    });

    if (category) {
      params.append('category', category);
    }

    if (sort) {
      params.append('sort', sort);
    }

    const response = await api.get(`/places/search?${params.toString()}`);
    return {
      data: response.data.data || [],
      meta: {
        total: response.data.data?.length || 0,
      },
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Быстрый поиск для автокомплита (только title и images)
 * @param {string} query - Поисковый запрос
 * @returns {Promise} - Promise with suggestions
 */
export const getSearchSuggestions = async (query) => {
  try {
    if (!query || query.trim().length < 2) {
      return { data: [] };
    }

    const response = await api.get('/places/search', {
      params: {
        query: query.trim(),
        limit: 5,
      },
    });

    return { data: response.data.data || [] };
  } catch (error) {
    return { data: [] };
  }
};

// Statistics tracking
/**
 * Track place statistics event
 * @param {number} placeId - Place ID
 * @param {string} event - Event type (view, click, website_click, phone_click, map_click, promo_copy)
 * @param {string} source - Source of event (search, home, category, nearby) - only for 'click' event
 */
export const trackPlaceEvent = async (placeId, event, source = null) => {
  try {
    const data = { placeId, event };
    if (source && event === 'click') {
      data.source = source;
    }

    await api.post('/place-stats/track', data);
  } catch (error) {
    // Не показываем ошибки трекинга пользователю
  }
};

/**
 * Track ad statistics event
 * @param {number} adId - Ad ID
 * @param {string} event - Event type (view, click, label_click)
 * @param {string} source - Source of event (home, categories, nearby) - optional
 */
export const trackAdEvent = async (adId, event, source = null) => {
  try {
    const data = { adId, event };
    if (source) {
      data.source = source;
    }
    await api.post('/ad-stats/track', data);
  } catch (error) {
    // Silently handle tracking errors
  }
};

/**
 * Get similar places
 * @param {number} placeId - Place ID
 * @returns {Promise} - Promise with similar places
 */
export const getSimilarPlaces = async (placeId) => {
  try {
    const response = await api.get(`/places/${placeId}/similar`);
    return response.data;
  } catch (error) {
    return { data: [] };
  }
};

// UI Settings
export const getUiSettings = async () => {
  try {
    const response = await api.get('/ui-setting?populate=*');
    return response.data;
  } catch (error) {
    return { data: null };
  }
};

// Collections
export const getCollections = async () => {
  try {
    const response = await api.get('/collections?filters[published][$eq]=true&sort=order:asc&populate=image');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCollectionBySlug = async (slug) => {
  try {
    const response = await api.get(
      `/collections?filters[published][$eq]=true&filters[slug][$eq]=${slug}&populate[0]=image&populate[1]=places.images&populate[2]=places.category`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Saved Collections
export const addToSavedCollections = async (collectionId) => {
  try {
    const response = await api.post('/saved-collections', { collectionId });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const removeFromSavedCollections = async (savedCollectionId) => {
  try {
    const response = await api.delete(`/saved-collections/${savedCollectionId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getSavedCollections = async () => {
  try {
    const response = await api.get('/saved-collections/my');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const checkIsSavedCollection = async (collectionId) => {
  try {
    const response = await api.get(`/saved-collections/check/${collectionId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Giveaways
export const getGiveaways = async () => {
  try {
    const response = await api.get('/giveaways?populate=image,winners');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getGiveawayBySlug = async (slug) => {
  try {
    const response = await api.get(`/giveaways/${slug}?populate=image,winners`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const participateInGiveaway = async (giveawayId) => {
  try {
    const response = await api.post(`/giveaways/${giveawayId}/participate`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Розыгрыши пользователя: участие и победы. Победа приходит и после
// завершения розыгрыша — см. контроллер myGiveaways
export const getMyGiveaways = async () => {
  try {
    const response = await api.get('/giveaways/my');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const checkGiveawayParticipation = async (giveawayId) => {
  try {
    const response = await api.get(`/giveaways/${giveawayId}/check-participation`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Routes
export const getRoutes = async () => {
  try {
    const response = await api.get('/routes?filters[published][$eq]=true&sort=createdAt:desc&fields[0]=title&fields[1]=slug&fields[2]=description&fields[3]=duration&fields[4]=distance&fields[5]=rating&populate[image][fields][0]=url&populate[tags][fields][0]=name&populate[stops][fields][0]=id');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getRouteBySlug = async (slug) => {
  try {
    const response = await api.get(
      `/routes?filters[published][$eq]=true&filters[slug][$eq]=${slug}&populate[image][fields][0]=url&populate[tags][fields][0]=name&populate[stops][fields][0]=order&populate[stops][fields][1]=description&populate[stops][fields][2]=navigationHint&populate[stops][fields][3]=task&populate[stops][fields][4]=entryFee&populate[stops][fields][5]=travelTime&populate[stops][fields][6]=audioTitle&populate[stops][fields][7]=imagesCaption&populate[stops][populate][place][fields][0]=title&populate[stops][populate][place][fields][1]=slug&populate[stops][populate][place][fields][2]=latitude&populate[stops][populate][place][fields][3]=longitude&populate[stops][populate][place][fields][4]=shortDescription&populate[stops][populate][place][fields][5]=metro&populate[stops][populate][place][fields][6]=address&populate[stops][populate][place][fields][7]=workingHours&populate[stops][populate][place][populate][images][fields][0]=url&populate[stops][populate][images][fields][0]=url&populate[stops][populate][audio]=true`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getRouteSavers = async (routeId) => {
  try {
    const response = await api.get(`/routes/${routeId}/savers?limit=5`);
    return response.data;
  } catch (error) {
    return { data: { users: [], count: 0 } };
  }
};

// ── Прохождение маршрута ────────────────────────────────────────────────────
// Раньше жило только в localStorage: цифры разнились на разных устройствах
// и гибли при чистке кеша. Сбои сети не должны ронять прогулку, поэтому
// чтение возвращает null, а запись — false вместо исключения

/** Прогресс по маршруту: { routeId } или { userRouteId } */
export const getRouteProgress = async ({ routeId, userRouteId }) => {
  try {
    const qs = routeId ? `routeId=${routeId}` : `userRouteId=${userRouteId}`;
    const response = await api.get(`/route-progress/mine?${qs}`);
    return response.data?.data || null;
  } catch (error) {
    return null;
  }
};

/** Создать или обновить прогресс. Возвращает true при успехе */
export const saveRouteProgress = async (payload) => {
  try {
    await api.put('/route-progress', payload);
    return true;
  } catch (error) {
    return false;
  }
};

/** Сбросить прогресс — «начать заново» */
/**
 * Сбросить прогресс — «начать заново».
 *
 * Параметры в query, а не в теле: тело у DELETE ненадёжно — Koa его
 * не разбирал, и ручка отвечала 400. Сброс молча не срабатывал,
 * а экран финиша возвращался.
 */
export const resetRouteProgress = async ({ routeId, userRouteId }) => {
  try {
    const qs = routeId ? `routeId=${routeId}` : `userRouteId=${userRouteId}`;
    await api.delete(`/route-progress/reset?${qs}`);
    return true;
  } catch (error) {
    return false;
  }
};

export const addToSavedRoutes = async (routeId) => {
  try {
    const response = await api.post('/saved-routes', { routeId });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const removeFromSavedRoutes = async (savedRouteId) => {
  try {
    const response = await api.delete(`/saved-routes/${savedRouteId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getSavedRoutes = async () => {
  try {
    const response = await api.get('/saved-routes/my');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const checkIsSavedRoute = async (routeId) => {
  try {
    const response = await api.get(`/saved-routes/check/${routeId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// User Routes (личные маршруты из избранного)
export const getUserRoutes = async () => {
  try {
    const response = await api.get('/user-routes/my');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const createUserRoute = async (routeData) => {
  try {
    const response = await api.post('/user-routes', routeData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateUserRouteDate = async (id, scheduledDate, scheduledTime) => {
  try {
    const response = await api.patch(`/user-routes/${id}`, { scheduledDate, scheduledTime });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateUserRouteStops = async (id, stops, extraFields = {}) => {
  try {
    const response = await api.patch(`/user-routes/${id}`, { stops, ...extraFields });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteUserRoute = async (id) => {
  try {
    const response = await api.delete(`/user-routes/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export default api;
